/**
 * Tigrinya catalog (Ethiopic / Geʽez script) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Tigrinya (ትግርኛ) at a mathematician's register, sharing the Ethiopic script
 * added for Amharic. **Warrant → «ማረጋገጺ»** (justification/warrant — flagged). Scientific
 * loanwords are used where Tigrinya standardly borrows (logic → ሎጂክ, modal → ሞዳል).
 *
 * NOTE (honesty over guessing): Tigrinya's standardized mathematical/epistemic register is
 * thinner than Amharic's; several abstract terms here are best-effort and the catalog is
 * flagged "expert-review suggested" in `Agent9_Batch3_REPORT.md`. W/M grades stay Latin
 * (`::numbering-system/latn`). CLDR cardinal categories are one / other (`one` covers 0–1), so
 * `frameActive` declares those (plus an explicit `=0`). Per INV-STABLE-ID any correction
 * changes only the string and leaves every verdict fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const TI_CATALOG = {
    locale: 'ti',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'ምርመራ',
        [P2.sectionClaims]: 'ዝበሃላት',
        [P2.sectionIntegrity]: 'ምሉእነት',
        [P2.axisWarrantLabel]: 'ማረጋገጺ',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'ተመኩሮኣዊ',
        [P2.axisEmpiricalValue]: '{kind, select, defn {ትርጉም} interp {ምብራህ} testable {ክፍተን ዝኽእል} tested {ተፈቲኑ} refuted {ተነጺጉ} program {መደብ መጽናዕቲ} philos {ፍልስፍናዊ} other {ዘይተፈልጠ}}',
        [P2.axisModalLabel]: 'ሞዳል',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'ደረጃ ብርታዐ {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'ክንደሃስ ዝኽእል',
        [P2.stateRefuted]: 'ተነጺጉ',
        [P2.stateOutside]: 'ካብ ዶብ ወጻኢ',
        [P2.verdictConflict]: 'ምስ {constraints} ይጋጮ',
        [P2.verdictOutside]: '{missing} የድሊ',
        [P2.lossRegisterPlain]: 'ናብ ቀሊል ቋንቋ ተቐሊሉ — ገሊኡ ልክዕነት ተሪፉ።',
        [P2.lossRegisterKid]: 'ንናእሽቱ ኣንበብቲ ተቐሊሉ — ብዙሕ ዝርዝራት ተሪፉ።',
        [P2.lossProfileDetail]: 'ምሉእ ናይ ማረጋገጺ / ተመኩሮኣዊ / ሞዳል መግለጺ ኣብዚ ተጠቓሊሉ።',
        [P2.lossConflictDetail]: 'ዝርዝራት ግጭት ኣብዚ ራእይ ተሓጺሮም።',
        [P2.laneVerified]: 'ተረጋጊጹ',
        [P2.laneUnverified]: 'ኣይተረጋገጸን',
        [P2.laneConflict]: 'ግጭት ተረጋጊጹ',
        [P2.constraintLogic]: 'ሎጂክ',
        [P2.constraintLightBends]: 'ብርሃን ኣብ መዳይ ስሕበት ይጥወ',
        [P2.constraintParallelPostulate]: 'ናይ ትይዩ መስመራት ፖስቱላት',
        [P2.claimScalarGravity]: 'ስኬላር ናይ ስሕበት ጽንሰ-ሓሳብ',
        [P2.claimNonEuclidean]: 'ዘይኤውክሊዳዊ ጂኦመትሪ',
        [P2.claimSubstrateSr]: 'ናይ ፍሉይ ኣንጻርነት ንኡስ-ቀጸላ ትርጉም',
        [P2.edgeExtObservation]: 'ግዳማዊ ምልከታ',
        [P2.edgeResourceSample]: 'ዝተኻፈለ ናይ ቍጽሪ ሃብቲ',
        [P2.edgeHeuristicSample]: 'ሂዩሪስቲክ መምርሒ',
        [P2.edgeSharedSample]: 'ዝተኻፈለ መሰረት',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'ገደባት',
        [P2.sectionEdges]: 'ጫፋት',
        [P2.sectionFrame]: 'ቀጽሪ',
        [P2.sectionExposure]: 'ምቅላዕ',
        [P2.statusAdoptedFormal]: 'ተቐቢሉ (ወግዓዊ)',
        [P2.statusAdoptedInterpretive]: 'ተቐቢሉ (ተርጓማይ)',
        [P2.statusSuspended]: 'ተኣጊዱ',
        [P2.edgeEffectConstraint]: 'ገደብ ይፈጥር (ክነጽግ ይኽእል)',
        [P2.edgeEffectResource]: 'ሃብቲ — ኣብ ፍርድታት ጽልዋ የብሉን',
        [P2.edgeEffectHeuristic]: 'ሂዩሪስቲክ — ኣብ ፍርድታት ጽልዋ የብሉን',
        [P2.edgeEffectShared]: 'ዝተኻፈለ መሰረት — ኣብ ፍርድታት ጽልዋ የብሉን',
        [P2.frameActive]: '{count, plural, =0 {ዝኾነ ንጡፍ ገደብ የለን} one {# ንጡፍ ገደብ} other {# ንጡፋት ገደባት}}',
        [P2.exposureNone]: 'ዝኾነ ተርጓማይ ጥገኝነት የለን',
        [P2.exposureDep]: 'ኣብ {id} ይምርኮስ',
        [P2.laneTrackMachine]: 'ማሽን',
        [P2.laneTrackExpert]: 'ክኢላ',
        [P2.laneTrackCommunity]: 'ማሕበረሰብ',
        [P2.expertAudited]: 'ተመርሚሩ',
        [P2.expertInProgress]: 'ኣብ ሂደት',
        [P2.expertNotReviewed]: 'ኣይተገምገመን',
        [P2.communityFlags]: 'ምልክታት ኣለዉ',
        [P2.communityNone]: 'ምልክታት የለዉን',
        [P2.controlChallenge]: 'ተቓወም',
        [P2.controlActivate]: 'ኣንቅሕ',
        [P2.controlMakeInterpretive]: 'ተርጓማይ ግበሮ',
        [P2.controlImportEdge]: 'ጫፍ ኣእቱ',
        [P2.controlPromote]: 'ኣደይብ',
        [P2.controlUndo]: 'ምለስ',
        [P2.controlRedo]: 'ድገም',
        [P2.controlRegister]: 'መዝግብ',
        [P2.promoteRejected]: 'ምድያብ ተነጺጉ',
        [P2.promoteAccepted]: 'ምድያብ ተቐቢሉ',
        [P2.promoteReasonRefutationTerminal]: 'መረጋገጺ ንምንጻግ ክፍውስ ኣይክእልን።',
        [P2.promoteReasonKeystone]: 'ቁልፊ ምድያብ ተመኩሮኣዊ ደገፍ የድሊ።',
        [P2.promoteReasonEmpiricalNotFixed]: 'እቲ ተመኩሮኣዊ ዓይነት ኣይተወሰነን።',
        [P2.promoteReasonModalNotFixed]: 'ናይ ሞዳል ደረጃ ኣይተወሰነን።',
        [P2.promoteReasonWarrantNotImproved]: 'ናይ መረጋገጺ ደረጃ ኣይተመሓየሸን።',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=ti.js.map